import { createSelector } from 'reselect';
import { roundValue } from '@ynomia/core/dist/utils';
import {
  getAssetManagerFilters,
  getAssetManagerSelectedAssetTypeId,
  getAssetTypes,
  getSelectedAssetType,
} from '..';
import { keyAssetsByStatus, isFilteredByDateFilter } from '../../utils';
import { getFilteredAssets, getFilteredAssetsContext } from '.';
import { RawObservationRecord, StatusSummaryConfig } from '../../config/types';
import map from '../map';

const getFilteredAssetsForAssetCurrentStatusSummary = createSelector(
  [
    getAssetManagerFilters,
    getFilteredAssetsContext,
  ],
  (
    assetManagerFilters,
    filteredAssetsContext,
  ) => {
    const {
      searchTags,
      searchText,
      dateFilter,
      observationTypeFilter,
      selectedLayerIds,
      selectedBandIds,
      tableFilter,
      openedLayerId,
      multiSearchModeFilter,
      fieldFilters,
    } = assetManagerFilters;

    const filteredAssets = getFilteredAssets(filteredAssetsContext, {
      searchTags,
      searchText,
      dateFilter,
      observationTypeFilter,
      selectedLayerIds,
      selectedBandIds,
      tableFilter,
      openedLayerId,
      multiSearchModeFilter,
      fieldFilters,
    });
    if (dateFilter?.length !== 2) return filteredAssets;
    return filteredAssets
      .filter((asset) => {
        const obs = asset.observations?.find(({ to }) => to === asset.status);
        const date = obs?.date;
        const dateFormat = date && new Date(date);
        return dateFormat && isFilteredByDateFilter(dateFormat, dateFilter);
      });
  },
);

const getFilteredAssetsForAssetStatusSummary = createSelector(
  [
    getAssetManagerFilters,
    getFilteredAssetsContext,
  ],
  (
    assetManagerFilters,
    filteredAssetsContext,
  ) => {
    const {
      searchTags,
      searchText,
      dateFilter,
      observationTypeFilter,
      selectedLayerIds,
      selectedBandIds,
      tableFilter,
      openedLayerId,
      multiSearchModeFilter,
      fieldFilters,
    } = assetManagerFilters;
    const filteredAssets = getFilteredAssets(filteredAssetsContext, {
      searchTags,
      searchText,
      dateFilter,
      observationTypeFilter,
      selectedLayerIds,
      selectedBandIds,
      tableFilter,
      openedLayerId,
      multiSearchModeFilter,
      fieldFilters,
    });
    return filteredAssets.sort((a, b) => String(a.label).localeCompare(String(b.label)));
  },
);

const getObservationStatusUpdateKeyedByStatusId = createSelector(
  [getAssetManagerFilters, getFilteredAssetsForAssetStatusSummary],
  (assetManagerFilters, assetsArray) => {
    const { dateFilter } = assetManagerFilters;
    const observationStatusUpdateKeyedByStatuses = new Map<string, Array<RawObservationRecord>>();

    if (assetsArray) {
      assetsArray.forEach((asset) => {
        const { observations } = asset;
        // Omit repeat observations within the same asset
        const uniqueStatusIds = new Set<string>();
        observations?.forEach((obs) => {
          const { date } = obs;
          const dateCheck = (dateFilter?.length !== 2)
            || (date && isFilteredByDateFilter(new Date(date), dateFilter));
          if (obs.to && !uniqueStatusIds.has(obs.to) && dateCheck) {
            uniqueStatusIds.add(obs.to);
            const record = observationStatusUpdateKeyedByStatuses.get(obs.to);
            if (record === undefined) {
              observationStatusUpdateKeyedByStatuses.set(obs.to, [obs]);
            } else {
              record.push(obs);
            }
          }
        });
      });
    }

    return observationStatusUpdateKeyedByStatuses;
  },
);

const getAssetsKeyedByStatusForCurrentAssetStatusSummary = createSelector(
  [getAssetTypes, getFilteredAssetsForAssetCurrentStatusSummary],
  keyAssetsByStatus,
);

export const getAssetStatusSummary = createSelector(
  [
    getAssetManagerFilters,
    getAssetManagerSelectedAssetTypeId,
    getAssetsKeyedByStatusForCurrentAssetStatusSummary,
    getFilteredAssetsForAssetStatusSummary,
    getSelectedAssetType,
    getObservationStatusUpdateKeyedByStatusId,
  ],
  (
    assetManagerFilters,
    selectedAssetTypeId,
    assetsKeyedByStatus,
    assetsArray,
    selectedAssetType,
    observationStatusUpdateKeyedByStatuses,
  ) => {
    const { dateFilter } = assetManagerFilters;

    if (!selectedAssetTypeId || !assetsArray) {
      return [];
    }
    const { statuses } = selectedAssetType;
    const hasDateFilter = dateFilter?.length === 2;

    return statuses ? statuses.map((status) => {
      const {
        id, label, color, order,
      } = status;
      const currentOccurrence = assetsKeyedByStatus.get(id)?.length || 0;
      const assetCount = assetsArray?.length;
      const defaultCumulativeOccurrence = hasDateFilter ? 0 : assetCount;
      const cumulativeOccurrence = status.default
        ? defaultCumulativeOccurrence
        : (observationStatusUpdateKeyedByStatuses.get(id)?.length || 0);
      const currentOccurrencePercentage = assetCount && roundValue(
        (currentOccurrence * 100) / assetCount,
        1,
      );
      const cumulativeOccurrencePercentage = assetCount && roundValue(
        (cumulativeOccurrence * 100) / assetCount,
        1,
      );
      return {
        id,
        label,
        color,
        currentOccurrence,
        currentOccurrencePercentage,
        cumulativeOccurrence,
        cumulativeOccurrencePercentage,
        order,
      };
    }) : [];
  },
);

export const getStatusSummaryConfigByAssetManagerId = createSelector(
  [
    map.features,
  ],
  (
    features,
  ): Map<string, { [key: string]: StatusSummaryConfig }> => {
    if (!features) return new Map();
    const featuresKeys = Object.keys(features);
    return new Map(featuresKeys.map((key) => {
      const statusSummaryKeyedByType: { [key: string]: StatusSummaryConfig } = {};
      const feature = features[key];
      const assetTypes = Object.keys(feature?.summaries || {});
      assetTypes.forEach((assetType) => {
        statusSummaryKeyedByType[assetType] = feature?.summaries[assetType].find(
          f => f.type === 'status_summary',
        ) as StatusSummaryConfig;
      });
      return [key, statusSummaryKeyedByType];
    }));
  },
);

export const getStatusSummaryConfig = createSelector(
  [
    map.assetManagerId,
    getAssetManagerSelectedAssetTypeId,
    getStatusSummaryConfigByAssetManagerId,
  ],
  (
    assetManagerId,
    selectedAssetTypeId,
    statusSummaryByFeature,
  ) => {
    const config = statusSummaryByFeature.get(assetManagerId);
    if (!config) return undefined;
    return config[selectedAssetTypeId];
  },
);

export const getStatusSummaryMode = createSelector(
  [getStatusSummaryConfig],
  statusSummaryConfig => statusSummaryConfig?.mode ?? 'both',
);

export const getStatusSummaryStatusSections = createSelector(
  [getStatusSummaryConfig],
  statusSummaryConfig => statusSummaryConfig?.status_sections ?? false,
);

export const getStatusSummaryForceShowAll = createSelector(
  [getStatusSummaryConfig],
  statusSummaryConfig => statusSummaryConfig?.force_show_all ?? false,
);

export const getStatusSectionsKeyedByIndex = createSelector(
  [getStatusSummaryStatusSections],
  (statusSections) => {
    const statusSectionsKeyedByIndex = new Map<number, { title: string }>();

    if (statusSections) {
      statusSections.forEach((section) => {
        statusSectionsKeyedByIndex.set(section.index!, section);
      });
    }

    return statusSectionsKeyedByIndex;
  },
);
